import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2, Plus, X, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const CompanySearch = ({
    value,
    onChange,
    companies: externalCompanies,
    placeholder = 'Search company...',
    allowCreate = true,
    className = '',
    inputClassName = '',
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');
    const [allCompanies, setAllCompanies] = useState(externalCompanies || []);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', url: '' });
    const [creating, setCreating] = useState(false);

    const inputRef = useRef(null);
    const containerRef = useRef(null);
    // Use a ref so the outside-click handler always sees the current value (no stale closure)
    const modalOpenRef = useRef(false);

    const setModal = (val) => {
        modalOpenRef.current = val;
        setModalOpen(val);
    };

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });

    // Load companies on mount if not passed in
    useEffect(() => {
        if (!externalCompanies || externalCompanies.length === 0) {
            axios.get(`${API}/companies`, getAuthHeaders())
                .then(r => setAllCompanies(r.data))
                .catch(() => { });
        }
    }, []); // eslint-disable-line

    // Sync when parent pushes updated list
    useEffect(() => {
        if (externalCompanies) setAllCompanies(externalCompanies);
    }, [externalCompanies]);

    // Sync display label with selected value
    useEffect(() => {
        if (!value) { setSelectedLabel(''); setQuery(''); return; }
        const found = allCompanies.find(c => c.id === value);
        if (found) { setSelectedLabel(found.name); setQuery(found.name); }
    }, [value, allCompanies]);

    // Filter results as user types
    useEffect(() => {
        if (!query.trim()) { setResults([]); return; }
        const q = query.toLowerCase();
        setResults(
            allCompanies
                .filter(c => c.name?.toLowerCase().includes(q) || c.url?.toLowerCase().includes(q))
                .slice(0, 10)
        );
    }, [query, allCompanies]);

    // Close dropdown on outside click — uses ref so always current, no stale closure
    useEffect(() => {
        const handler = (e) => {
            // Never close if the create modal is open
            if (modalOpenRef.current) return;
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                if (value) {
                    const found = allCompanies.find(c => c.id === value);
                    setQuery(found ? found.name : '');
                } else {
                    setQuery('');
                }
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [value, allCompanies]);

    const handleSelect = (company) => {
        setSelectedLabel(company.name);
        setQuery(company.name);
        setOpen(false);
        onChange(company.id, company);
    };

    const handleClear = () => {
        setQuery(''); setSelectedLabel(''); setOpen(false);
        onChange(null, null);
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const openCreateModal = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCreateForm({ name: query.trim(), url: '' });
        setOpen(false);
        setModal(true);
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        if (!createForm.name.trim()) return;
        setCreating(true);
        try {
            const res = await axios.post(`${API}/companies`, createForm, getAuthHeaders());
            const newCompany = res.data;
            setAllCompanies(prev => [...prev, newCompany]);
            toast.success(`Company "${newCompany.name}" created`);
            setModal(false);
            setCreateForm({ name: '', url: '' });
            handleSelect(newCompany);
        } catch {
            toast.error('Failed to create company');
        } finally {
            setCreating(false);
        }
    };

    const noResults = !!query.trim() && results.length === 0;
    const showDropdown = open && (results.length > 0 || noResults);

    return (
        <>
            <div ref={containerRef} className={`relative ${className}`}>
                {/* Search input */}
                <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <Input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        className={`pl-9 pr-8 bg-white ${inputClassName}`}
                        autoComplete="off"
                    />
                    {value && selectedLabel ? (
                        <button type="button" onClick={handleClear}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                            <X className="w-4 h-4" />
                        </button>
                    ) : (
                        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    )}
                </div>

                {/* Dropdown results */}
                {showDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                        {results.length > 0 && (
                            <ul className="max-h-60 overflow-y-auto divide-y divide-gray-50">
                                {results.map(c => (
                                    <li
                                        key={c.id}
                                        className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-blue-50 transition-colors ${value === c.id ? 'bg-blue-50' : ''}`}
                                        onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                                    >
                                        <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                                            {c.url && <p className="text-xs text-gray-400 truncate">{c.url}</p>}
                                        </div>
                                        {value === c.id && (
                                            <span className="ml-auto text-xs text-blue-600 font-semibold shrink-0">Selected</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {noResults && (
                            <div className="px-3 py-2.5 text-sm text-gray-500 border-b border-gray-100">
                                No match for <strong>"{query}"</strong>
                            </div>
                        )}

                        {noResults && allowCreate && (
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-3 hover:bg-emerald-50 text-emerald-700 font-medium text-sm transition-colors"
                                onMouseDown={openCreateModal}
                            >
                                <Plus className="w-4 h-4 shrink-0" />
                                <span>Create new company <strong>"{query}"</strong></span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ── Create Company Modal ── */}
            <Dialog open={modalOpen} onOpenChange={(o) => { if (!creating) setModal(o); }}>
                <DialogContent className="sm:max-w-sm" onPointerDownOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-emerald-600" />
                            Create New Company
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateCompany} className="space-y-4 pt-1">
                        <div className="space-y-2">
                            <Label htmlFor="cs-name">
                                Company Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="cs-name"
                                placeholder="Acme Corp"
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                required
                                autoFocus
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cs-url">
                                Website URL{' '}
                                <span className="text-gray-400 font-normal">(optional)</span>
                            </Label>
                            <Input
                                id="cs-url"
                                placeholder="https://acme.com"
                                value={createForm.url}
                                onChange={(e) => setCreateForm({ ...createForm, url: e.target.value })}
                            />
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button
                                type="submit"
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                disabled={creating}
                            >
                                {creating
                                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating…</>
                                    : 'Create & Select'
                                }
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setModal(false)}
                                disabled={creating}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CompanySearch;
